import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { makeStyles } from '@material-ui/styles';
import { Theme, Card, CardContent, Typography, Container, TextField, Grid, InputAdornment, IconButton, Modal, Button, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Tooltip } from '@material-ui/core';

import {
    find as _find,
    filter as _filter,
    get as _get,
    keys as _keys,
    capitalize as _capitalize,
    cloneDeep as _cloneDeep,
    trimEnd as _trimEnd,
    size,
  } from "lodash";

import Page from '../../components/page';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { FacilityActionType } from '../../store/facilities/facilities-action-types';
import { Navigations } from '../../settings/nav-config';
import CircularIndeterminate from '../../components/circular-indeterminate';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { green, grey } from '@material-ui/core/colors';
import { APIURL, client } from '../../services/at-app-api';
import { toast } from 'react-toastify';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import { TimeZoneOptions } from '../../settings/app';


const profileSchema = yup.object().shape({
  email: yup.string().email('Invalid email format').required('Email is required.'),
  firstName: yup.string(),
  lastName: yup.string(),
  phoneNumber: yup.string().required('Phone Number is required.'),
  timezone: yup.string(),
  facilityUid: yup.string(),
  password: yup.string(),
  confirmPassword: yup.string().test('passwords-match', 'Passwords must match', function(value) {
    return this.parent.password === value;
  }),
});

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  grid: {
    marginTop: theme.spacing(2)
  },
  card: {
    maxWidth: '100%',
  },
  form: {
    position: 'relative',
  },
  formHeader: {
      width: '100%',
      fontSize: '20px',
      marginBottom: theme.spacing(4),
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
}));

const ProfileDefault = (props: any) => {
  const {
    type,
  } = props;

  const dispatch = useDispatch();
  const [clinicOptions, setClinicOptions] = useState([] as any);

  const clinics = useSelector(
    (state) => _get(state, ["facilities", "clinics"]) || { data: [], total: 0 },
    shallowEqual
  );
  const userProfile = useSelector(
    (state) => _get(state, ["user", "profile"]) || null,
    shallowEqual
  );

  const { register, handleSubmit, errors, setValue, reset } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      password: '',
      confirmPassword: '',
      email: '',
      phoneNumber: '',
      timezone: '',
      mfaEnabled: false,
      facilityUid: '',
    },
    validationSchema: profileSchema,
  });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    phoneNumber: '',
    timezone: '',
    mfaEnabled: false,
    emailConfirmed: false,
    phoneNumberConfirmed: false,
  });

  useEffect(() => {
    if (userProfile) {
      const profileData = {
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        username: userProfile.username || '',
        password: '',
        confirmPassword: '',
        email: userProfile.email || '',
        phoneNumber: userProfile.phoneNumber || '',
        timezone: userProfile.timezone || '',
        mfaEnabled: !!userProfile.mfaEnabled,
        emailConfirmed: userProfile.emailConfirmed || false,
        phoneNumberConfirmed: userProfile.phoneNumberConfirmed || false,
      };
      setFormData(profileData);
      // Update react-hook-form values
      setValue('firstName', profileData.firstName);
      setValue('lastName', profileData.lastName);
      setValue('username', profileData.username);
      setValue('email', profileData.email);
      setValue('phoneNumber', profileData.phoneNumber);
      setValue('timezone', profileData.timezone);
      setValue('mfaEnabled', profileData.mfaEnabled);
      setValue('password', profileData.password);
      setValue('confirmPassword', profileData.confirmPassword);
    }
  }, [userProfile, setValue]);

  const handleCancel = () => {
    window.history.back();
  };

  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    
    dispatch({
      type: FacilityActionType.GetFacilities,
      payload: {
        type: type,
        subType: Navigations.facilities.clinics,
        targetType: Navigations.facilities.clinics,
        facilityUid: null,
        parentFacilityUid: null,
        parentUid: null,
        skip: 0,
        take: 0
      }
    });
    
    return () => {
    };
  }, [dispatch, type]);
  
  const classes = useStyles();

  
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? !!checked : value,
    }));
    // Sync with react-hook-form
    setValue(name as any, type === 'checkbox' ? !!checked : value);
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [verificationMethod, setVerificationMethod] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  
  const handleToggleVerifyContact = (type: string) => {
    const challengeMethod = type === 'Email' ? 'Email' : 'Phone';
    setVerificationMethod(challengeMethod);

    setIsLoading(true);
    // Call MFA challenge API
    client().post(`${APIURL}/api/account/mfa/challenge`, { method: challengeMethod })
      .then(response => {
        if (response.data?.status === 'sent') {
          toast.info(`Verification ${challengeMethod} sent. Please check your ${challengeMethod} to complete verification.`);
          setVerificationCode('');
          setModalOpen(true);
        }
      })
      .catch(error => {
        toast.error(`Error sending verification ${challengeMethod}: ${error.message}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleVerifyContact = () => {
    setIsLoading(true);
    // Call MFA verify API
    client().post(`${APIURL}/api/account/mfa/verify`, { code: verificationCode.trim(), method: verificationMethod })
      .then(response => {
        console.log('MFA verify response:', response);
        if (response.data?.valid === true) {
          toast.success(`${_capitalize(verificationMethod)} verified successfully.`);

          let fieldName: string = '';
          if (verificationMethod === 'Email') fieldName = 'email';
          if (verificationMethod === 'Phone') fieldName = 'phoneNumber';

          setFormData(prev => ({
            ...prev,
            [`${fieldName}Confirmed`]: true,
          }));
          setModalOpen(false);
        } else {
          toast.error(`Verification failed. Please try again.`);
        }
      })
      .catch(error => {
        const errorMessage = error.response?.data?.error_description || error.message;
        toast.error(`Error verifying ${verificationMethod === 'email' ? 'Email' : 'Phone Number'}: ${errorMessage}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleResendContact = () => {
    if (!verificationMethod) return;
    setIsLoading(true);
    // Re-send MFA challenge for the current method
    const methodLabel = verificationMethod === 'email' ? 'Email' : verificationMethod === 'phone' ? 'Phone' : verificationMethod;
    client().post(`${APIURL}/api/account/mfa/challenge`, { method: _capitalize(methodLabel) })
      .then(response => {
        if (response.data?.status === 'sent') {
          toast.info(`Verification ${_capitalize(methodLabel)} resent. Please check your ${_capitalize(methodLabel)}.`);
        } else {
          toast.warn(`Could not resend to ${_capitalize(methodLabel)}. Please try again.`);
        }
      })
      .catch(error => {
        toast.error(`Error resending to ${_capitalize(methodLabel)}: ${error.message}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleClickSave = (data: any) => {
    setIsLoading(true);
    // Call update profile API
    const updateData: any = {
      uid: userProfile?.uid,
      firstName: formData.firstName,
      lastName: formData.lastName,
      username: formData.username,
      password: formData.password ? formData.password : undefined,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      timezone: formData.timezone,
      mfaEnabled: !!formData.mfaEnabled,
    };
    client().put(`${APIURL}/api/user`, updateData)
      .then(response => {
        toast.success('Profile updated successfully.');
        // Optionally update local state or context with new profile data
      })
      .catch(error => {
        toast.error(`Error updating profile: ${error.message}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Page
      className={classes.root}
      title="Edit Profile"
    >
      <Container maxWidth={false}>

        <Card className={classes.card}>
          <CardContent>
            <div className={classes.formHeader}>Edit Profile</div>
            {
              isLoading && (
              <CircularIndeterminate/>
              )
            }
            
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      inputRef={register}
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      inputRef={register}
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined" fullWidth onChange={handleInputChange}
                      value={formData.username} name="username" label="Username (Read Only)" />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="password"
                      label="Password"
                      type="password"
                      inputRef={register}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="confirmPassword"
                      label="Confirm Password"
                      type="password"
                      inputRef={register}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      label="Email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      inputRef={register({ required: 'Email is required.' })}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      InputProps={{
                        endAdornment: (formData.email && formData.email === (userProfile?.email || '')) ? (
                          <InputAdornment position="end">
                            <Tooltip title={formData.emailConfirmed ? 'Email verified' : 'Verify Email'} arrow placement='left'>
                              <IconButton
                                aria-label="verify email"
                                onClick={() => {
                                  if (formData.email && formData.email.length > 0 && !formData.emailConfirmed) {
                                    handleToggleVerifyContact('Email');
                                  }
                                }}
                              >
                                <VerifiedUserIcon style={{ color: formData.emailConfirmed ? green[500] : grey[300] }} />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      label="Phone"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      inputRef={register({ required: 'Phone Number is required.' })}
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber?.message}
                      InputProps={{
                        endAdornment: (formData.phoneNumber && formData.phoneNumber === (userProfile?.phoneNumber || '')) ? (
                          <InputAdornment position="end">
                            <Tooltip title={formData.phoneNumberConfirmed ? 'Phone verified' : 'Verify Phone'} arrow placement='left'>
                              <IconButton
                                aria-label="verify phone"
                                onClick={() => {
                                  if (formData.phoneNumber && formData.phoneNumber.length > 0 && !formData.phoneNumberConfirmed) {
                                    handleToggleVerifyContact('Phone');
                                  }
                                }}
                              >
                                <VerifiedUserIcon style={{ color: formData.phoneNumberConfirmed ? green[500] : grey[300] }} />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl variant="outlined" fullWidth error={!!errors.timezone}>
                      <InputLabel id="demo-simple-select-outlined-label">Time Zone</InputLabel>
                      <Select
                        name="timezone"
                        value={formData.timezone}
                        onChange={(e) => handleInputChange(e as React.ChangeEvent<HTMLInputElement>)}
                        label="Time Zone"
                        inputRef={register}
                      >
                        {TimeZoneOptions.map((timezone: { value: string; label: string }) => (
                          <MenuItem key={timezone.value} value={timezone.value}>
                            {timezone.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <Typography variant="subtitle1" >
                      Multi-Factor Authentication
                    </Typography>
                    <FormControlLabel
                      control={<Switch checked={!!formData.mfaEnabled} onChange={(e) => handleInputChange(e as React.ChangeEvent<HTMLInputElement>)} name="mfaEnabled" color="primary" />}
                      label={formData.mfaEnabled ? 'On' : 'Off'}
                    />
                  </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid container spacing={2} style={{ marginTop: 16 }} justifyContent='flex-end'>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => window.history.back()}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit(handleClickSave)}
                >
                  Save
                </Button>
              </Grid>
            </Grid>

          </CardContent>
        </Card>
      </Container>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={modalOpen}
        // onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={modalOpen}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Verify {verificationMethod === 'email' ? 'Email Address' : 'Phone Number'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Please enter the verification code sent to your {verificationMethod === 'email' ? 'Email Address' : 'Phone Number'}.
              </Typography>
              <TextField variant="outlined" fullWidth label="Verification Code" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} />
              <Grid container spacing={2} style={{ marginTop: 8 }} justifyContent='flex-end'>
                <Grid item style={{ marginRight: 'auto' }}>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={handleResendContact}
                    style={{ textTransform: 'none', textDecoration: 'underline' }}
                  >
                    Resend Code
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleVerifyContact}
                  >
                  Verify
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={() => setModalOpen(false)}
                  >
                  Cancel
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>
      </Modal>
    </Page>
  );
}

export default ProfileDefault;

